// 261010 P9D D5 — hàm THUẦN (0 antd/router) cho: thanh cấu trúc video (cắt / gộp / chèn / xoá), banner NEEDS_SPLIT còn sau chặng 1, giờ cảnh.
// Cắt/gộp/chèn/xoá gọi THẲNG route sẵn có (`POST /events/:id/split|merge-next|insert-after`, `DELETE /events/:id`) — không đi qua nháp `workflowEditOps`.
import type { EventPlanScene, FilmEvent, FilmEventBeatMeta } from '../../types/film';
import type { WorkflowStepState } from '../../types/filmWorkflow';
import { canMergeV2, isBeatMetaV2, validSplitPoints } from './beatSequenceUtils';

// ───────────── NEEDS_SPLIT còn sau chặng 1 (Rev 3 mục 4) ─────────────
interface SplitProbe {
  eventOrder: number;
  generation?: { block?: { code?: string } | null } | null;
  stepFlags?: Partial<Record<string, Array<{ code?: string }>>> | null;
}
const isNeedsSplit = (e: SplitProbe): boolean => {
  if (e.generation?.block?.code === 'NEEDS_SPLIT') return true;
  const flags = e.stepFlags ? Object.values(e.stepFlags).flatMap((x) => x ?? []) : [];
  return flags.some((f) => f?.code === 'NEEDS_SPLIT');
};
/** Video còn cờ NEEDS_SPLIT (nguồn: `generation.block` của `/events` hoặc cờ bền `stepFlags` của `workflow-status`). */
export function needsSplitRemainingOf(events: SplitProbe[] | null | undefined): { count: number; eventOrders: number[] } {
  const orders = Array.from(new Set((events ?? []).filter(isNeedsSplit).map((e) => e.eventOrder))).sort((a, b) => a - b);
  return { count: orders.length, eventOrders: orders };
}

export interface NeedsSplitBanner { text: string; confirmNote: string | null }
/** Banner bước 1: chỉ khi bước 1 đã có kết quả (READY/STALE/APPROVED) mà còn video vượt trần. Nút «Chạy lại Phân đoạn» do component vẽ — gọi RUN_STAGE1, không tự chạy. */
export function needsSplitBannerOf(scriptState: WorkflowStepState | string, rem: { count: number; eventOrders?: number[] }, approved: boolean): NeedsSplitBanner | null {
  if (!['READY', 'STALE', 'APPROVED'].includes(String(scriptState))) return null;
  if (rem.count <= 0) return null;
  return {
    text: `${rem.count} video vẫn dài quá trần, máy không chia được.`,
    confirmNote: approved ? 'Phân đoạn đã duyệt — chạy lại rồi duyệt Phân đoạn sẽ thành cũ, phải duyệt lại.' : null,
  };
}

// ───────────── Cắt / gộp / chèn / xoá ─────────────
export type SplitChoices = { mode: 'beat' | 'shot'; points: number[] };
/** v2 cắt SAU nhịp k ở ranh giới hợp lệ (`keepBeatCount`); v1 cắt SAU shot k (`keepShotCount`). */
export function splitChoicesOf(ev: Pick<FilmEvent, 'beatMeta' | 'shots'>): SplitChoices {
  const bm = (ev.beatMeta ?? null) as FilmEventBeatMeta | null;
  if (isBeatMetaV2(bm)) return { mode: 'beat', points: validSplitPoints(bm?.actionBeats || [], bm?.sourceLines || []) };
  const n = (ev.shots ?? []).length;
  return { mode: 'shot', points: n > 1 ? Array.from({ length: n - 1 }, (_, i) => i + 1) : [] };
}

/** Gộp với video kế tiếp CÙNG cảnh (BE `merge-next`); luật v1/v2 + hook dùng chung `canMergeV2`. */
export function mergeNextOf(scenes: Array<Pick<EventPlanScene, 'events'>>, eventId: string): { ok: boolean; reason?: string } {
  for (let si = 0; si < scenes.length; si++) {
    const evs = scenes[si].events || [];
    const ei = evs.findIndex((e) => e.id === eventId);
    if (ei < 0) continue;
    if (ei >= evs.length - 1) return { ok: false, reason: 'Đây là video cuối của cảnh — chỉ gộp được với video kế tiếp cùng cảnh.' };
    return canMergeV2(scenes as any, { sceneIdx: si, eventIdx: ei }, { sceneIdx: si, eventIdx: ei + 1 });
  }
  return { ok: false, reason: 'Không tìm thấy video trong plan.' };
}

export function structDisabledReason(o: { editing: boolean; busy: boolean }): string | null {
  if (o.editing) return 'Đang có bản sửa chưa lưu — Lưu hoặc huỷ bản sửa trước khi đổi cấu trúc video.';
  if (o.busy) return 'Đang thực hiện thao tác khác — đợi xong.';
  return null;
}

type StructKind = 'split' | 'merge' | 'insert' | 'delete';
const HASH = 'Việc này đổi hash Phân đoạn: bản đã duyệt thành cũ, phải duyệt lại.';
export const STRUCT_CONFIRM: Record<StructKind, { title: string; content: string; okText: string }> = {
  split: { title: 'Cắt đôi video này?', content: `Thoại đi theo nhịp/shot đứng trước nó. Thời lượng, sổ liền mạch và prompt của hai nửa bị xoá — phải làm lại. ${HASH}`, okText: 'Cắt đôi' },
  merge: { title: 'Gộp với video kế tiếp?', content: `Gộp hành động, shot và thoại. Thời lượng, sổ liền mạch và prompt bị xoá — phải làm lại. ${HASH}`, okText: 'Gộp' },
  insert: { title: 'Chèn video trống phía sau?', content: `Tạo một video trống ngay sau video này để bạn viết tay. ${HASH}`, okText: 'Chèn' },
  delete: { title: 'Xoá video này?', content: `Xoá luôn shot và thoại của video. ${HASH}`, okText: 'Xoá' },
};
export const STRUCT_DONE: Record<StructKind, string> = {
  split: 'Đã cắt đôi video.', merge: 'Đã gộp video.', insert: 'Đã chèn video trống.', delete: 'Đã xoá video.',
};

// ───────────── Giờ trong ngày của cảnh (SC2) — giá trị khớp UI cũ ─────────────
export const SCENE_TIME_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'DAWN', label: '🌅 Bình minh' },
  { value: 'MORNING', label: '🌤 Sáng' },
  { value: 'DAY', label: '☀️ Ban ngày' },
  { value: 'AFTERNOON', label: '🌇 Chiều' },
  { value: 'DUSK', label: '🌆 Hoàng hôn' },
  { value: 'EVENING', label: '🌃 Tối' },
  { value: 'NIGHT', label: '🌙 Đêm' },
];
export function sceneTimeToastOf(staleEvents: number): string {
  return staleEvents > 0
    ? `Đã đổi giờ trong ngày — ${staleEvents} video cần dựng lại prompt trước khi gen.`
    : 'Đã đổi giờ trong ngày của cảnh.';
}

// ───────────── Shot (bước 4): ↑ ↓ xoá thêm — route có sẵn `PUT events/:id/shots/reorder`, `DELETE event-shots/:id`, `POST events/:id/shots` ─────────────
type ShotLite = { id: string; orderIndex: number };
const byOrder = <T extends ShotLite>(shots: T[]): T[] => [...shots].sort((a, b) => a.orderIndex - b.orderIndex);
/** `i` = vị trí (0-based) trong danh sách đang HIỆN (đã sắp theo orderIndex). */
export function shotMoveFlagsOf(shots: ShotLite[], i: number): { canUp: boolean; canDown: boolean } {
  const n = shots.length;
  return { canUp: n > 1 && i > 0, canDown: n > 1 && i < n - 1 };
}
/** Danh sách id mới sau khi đổi chỗ shot `id` với shot kề; ngoài biên / không thấy ⇒ null (không gọi route). */
export function reorderIdsOf(shots: ShotLite[], id: string, dir: -1 | 1): string[] | null {
  const ids = byOrder(shots).map((s) => s.id);
  const i = ids.indexOf(id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= ids.length) return null;
  [ids[i], ids[j]] = [ids[j], ids[i]];
  return ids;
}
/** Thao tác shot gọi THẲNG route (id shot đổi ở server) ⇒ không làm khi còn nháp chưa lưu. */
export function shotStructDisabledReason(o: { dirty: boolean; busy: boolean }): string | null {
  if (o.dirty) return 'Đang có bản sửa chưa lưu — Lưu hoặc huỷ bản sửa trước khi đổi thứ tự / thêm / xoá shot.';
  if (o.busy) return 'Đang thực hiện thao tác khác — đợi xong.';
  return null;
}
const STALE_SB = 'Storyboard và diễn xuất của video này có thể thành cũ (kiểm lại sau khi đổi).';
export const SHOT_STRUCT_CONFIRM = {
  delete: { title: 'Xoá shot này?', content: `Shot bị xoá khỏi video; thời lượng video tính lại. ${STALE_SB}`, okText: 'Xoá' },
  add: { title: 'Thêm một shot?', content: `Thêm một shot trống vào cuối video; thời lượng video tính lại. ${STALE_SB}`, okText: 'Thêm' },
} as const;
